import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Icons } from '../utils/icons';
import api from '../utils/api';
import { fontStack, glassCard, dInput, btnPrimary, btnSmall, btnDanger, btnGhost } from '../styles/shared';
import ConfirmModal from './ConfirmModal';
import useConfirm from '../hooks/useConfirm';

const INTERVALS = [
    { key: 'weekly',   label: 'Weekly',    days: 7,   period: 'week' },
    { key: 'biweekly', label: 'Bi-weekly', days: 14,  period: '2 weeks' },
    { key: 'monthly',  label: 'Monthly',   days: 30,  period: 'month' },
    { key: '3months',  label: '3 Months',  days: 90,  period: '3 months' },
    { key: '1year',    label: '1 Year',    days: 365, period: 'year' },
];

const STATUS_COLORS = {
    active:   { bg: 'var(--success-bg)', color: 'var(--success)', border: 'var(--success-border)' },
    pending:  { bg: 'var(--warning-bg, rgba(255,165,0,0.1))', color: 'var(--warning, #ffa500)', border: 'var(--warning-border, rgba(255,165,0,0.3))' },
    past_due: { bg: 'var(--warning-bg, rgba(255,165,0,0.1))', color: 'var(--warning, #ffa500)', border: 'var(--warning-border, rgba(255,165,0,0.3))' },
    canceled: { bg: 'var(--error-bg)', color: 'var(--error)', border: 'var(--error-border)' },
    expired:  { bg: 'var(--error-bg)', color: 'var(--error)', border: 'var(--error-border)' },
};

const card = {
    ...glassCard,
    padding: '24px',
    marginBottom: '20px',
};

const label = {
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--text-muted)',
    marginBottom: '6px',
    display: 'block',
};

export default function SubscriptionsTab({ channel }) {
    const { token } = useAuth();
    const toast = useToast();
    const { confirm, confirmProps } = useConfirm();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [config, setConfig] = useState(null);
    const [plans, setPlans] = useState(
        INTERVALS.map(i => ({ interval_key: i.key, price_usd: '', enabled: false, whop_plan_id: '', original_price_usd: '', initial_price_usd: '', promo_code: '', show_discount_code: true }))
    );
    const [vipChatId, setVipChatId] = useState('');
    const [vipChatTitle, setVipChatTitle] = useState('');
    const [enabled, setEnabled] = useState(true);
    const [promoText, setPromoText] = useState('');
    const [ctaLabel, setCtaLabel] = useState('Subscribe Now');
    const [promoBanner, setPromoBanner] = useState('');
    const [uploadingBanner, setUploadingBanner] = useState(false);
    const [posting, setPosting] = useState(false);
    const bannerInputRef = useRef(null);

    // Subscribers
    const [subsTab, setSubsTab] = useState('active');
    const [subscribers, setSubscribers] = useState([]);
    const [subsTotal, setSubsTotal] = useState(0);
    const [subsPage, setSubsPage] = useState(1);
    const [subsLoading, setSubsLoading] = useState(false);
    const [expandedSub, setExpandedSub] = useState(null);
    const [subDetails, setSubDetails] = useState({});
    const [subDetailsLoading, setSubDetailsLoading] = useState({});

    // Expanded discount sections per plan
    const [expandedPlans, setExpandedPlans] = useState({});

    // VIP channel connect flow
    const [connectingVip, setConnectingVip] = useState(false);

    // Track saved state for unsaved changes detection
    const savedConfigRef = useRef(null);

    useEffect(() => {
        loadConfig();
        loadSubscribers(1, 'active');
    }, [channel]);

    // Poll for VIP channel detection while connecting
    useEffect(() => {
        if (!connectingVip) return;
        const interval = setInterval(async () => {
            try {
                const res = await api.post('/api/subscriptions/config/get', { token, channel_username: channel });
                const cfg = res.data.config;
                if (cfg && cfg.vip_chat_id) {
                    setConfig(cfg);
                    setVipChatId(cfg.vip_chat_id);
                    setVipChatTitle(cfg.vip_chat_title || '');
                    setConnectingVip(false);
                    toast.success('Subscription channel connected!');
                }
            } catch (err) {
                // ignore polling errors
            }
        }, 2000);
        return () => clearInterval(interval);
    }, [connectingVip, channel, token]);

    const loadConfig = async () => {
        setLoading(true);
        try {
            const res = await api.post('/api/subscriptions/config/get', { token, channel_username: channel });
            const cfg = res.data.config;
            const serverPlans = res.data.plans || [];

            if (cfg) {
                setConfig(cfg);
                setVipChatId(cfg.vip_chat_id || '');
                setVipChatTitle(cfg.vip_chat_title || '');
                setEnabled(cfg.enabled !== false);
                setPromoText(cfg.promo_text || '');
                setCtaLabel(cfg.cta_button_label || 'Subscribe Now');
                setPromoBanner(cfg.promo_banner_file_id || '');
            }

            // Merge server plans with interval definitions
            const mergedPlans = INTERVALS.map(i => {
                const sp = serverPlans.find(p => p.interval_key === i.key);
                return {
                    interval_key: i.key,
                    price_usd: sp ? String(sp.price_usd) : '',
                    enabled: sp ? sp.enabled : false,
                    whop_plan_id: sp ? sp.whop_plan_id : '',
                    original_price_usd: sp && sp.original_price_usd != null ? String(sp.original_price_usd) : '',
                    initial_price_usd: sp && sp.initial_price_usd != null ? String(sp.initial_price_usd) : '',
                    promo_code: sp ? (sp.promo_code || '') : '',
                    show_discount_code: sp ? (sp.show_discount_code !== false) : true,
                };
            });
            setPlans(mergedPlans);

            // Snapshot saved state for dirty detection
            savedConfigRef.current = {
                enabled: cfg ? cfg.enabled !== false : true,
                promoText: cfg?.promo_text || '',
                ctaLabel: cfg?.cta_button_label || 'Subscribe Now',
                promoBanner: cfg?.promo_banner_file_id || '',
                vipChatTitle: cfg?.vip_chat_title || '',
                plans: mergedPlans.map(p => ({ interval_key: p.interval_key, price_usd: p.price_usd, enabled: p.enabled, original_price_usd: p.original_price_usd, initial_price_usd: p.initial_price_usd, promo_code: p.promo_code, show_discount_code: p.show_discount_code })),
            };
        } catch (err) {
            console.error('Load config error:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadSubscribers = async (page, tab) => {
        const statusFilter = tab || subsTab;
        setSubsLoading(true);
        try {
            const res = await api.get(`/api/subscriptions/subscribers?channel=${encodeURIComponent(channel)}&page=${page}&per_page=20&status=${statusFilter}`);
            setSubscribers(res.data.subscribers || []);
            setSubsTotal(res.data.total || 0);
            setSubsPage(page);
        } catch (err) {
            console.error('Load subscribers error:', err);
        } finally {
            setSubsLoading(false);
        }
    };

    const switchSubsTab = (tab) => {
        setSubsTab(tab);
        setSubsPage(1);
        loadSubscribers(1, tab);
    };

    const toggleSubDetails = async (subId) => {
        if (expandedSub === subId) {
            setExpandedSub(null);
            return;
        }
        setExpandedSub(subId);
        if (subDetails[subId]) return; // already loaded
        setSubDetailsLoading(prev => ({ ...prev, [subId]: true }));
        try {
            const res = await api.get(`/api/subscriptions/subscriber-details?id=${subId}`);
            setSubDetails(prev => ({ ...prev, [subId]: res.data }));
        } catch (err) {
            console.error('Load subscriber details error:', err);
        } finally {
            setSubDetailsLoading(prev => ({ ...prev, [subId]: false }));
        }
    };

    const requestConnectVip = async () => {
        try {
            await api.post('/api/subscriptions/request-add-vip-channel', { token, channel_username: channel });
            setConnectingVip(true);
            toast.success('Check your Telegram for the connect button!');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to request channel setup');
        }
    };

    const handleBannerUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) { toast.error('Image too large (max 10MB)'); return; }
        setUploadingBanner(true);
        try {
            const fd = new FormData();
            fd.append('token', token);
            fd.append('file', file);
            fd.append('type', 'banner');
            const res = await api.post('/api/dashboard/upload', fd);
            setPromoBanner(res.data.file_id);
            toast.success('Banner uploaded');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Upload failed');
        } finally {
            setUploadingBanner(false);
            e.target.value = '';
        }
    };

    const handlePostPromo = async () => {
        if (!vipChatId) { toast.error('Connect a subscription channel first'); return; }
        setPosting(true);
        try {
            await api.post('/api/subscriptions/post-promo', { token, channel_username: channel });
            toast.success('Promo posted to channel!');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to post promo');
        } finally {
            setPosting(false);
        }
    };

    const handleSave = async () => {
        if (!vipChatId) {
            toast.error('Please enter a Channel Chat ID');
            return;
        }
        setSaving(true);
        try {
            const res = await api.post('/api/subscriptions/config/save', {
                token,
                channel_username: channel,
                vip_chat_id: Number(vipChatId),
                vip_chat_title: vipChatTitle,
                enabled,
                promo_text: promoText,
                cta_button_label: ctaLabel,
                promo_banner_file_id: promoBanner,
                plans: plans.map(p => {
                    const origVal = p.original_price_usd !== '' ? parseFloat(p.original_price_usd) : NaN;
                    const initVal = p.initial_price_usd !== '' ? parseFloat(p.initial_price_usd) : NaN;
                    return {
                        interval_key: p.interval_key,
                        price_usd: parseFloat(p.price_usd) || 0,
                        enabled: p.enabled,
                        original_price_usd: !isNaN(origVal) ? origVal : null,
                        initial_price_usd: !isNaN(initVal) ? initVal : null,
                        promo_code: p.promo_code || null,
                        show_discount_code: p.show_discount_code !== false,
                    };
                }),
            });
            if (res.data.warning) {
                toast.warn(res.data.warning);
            } else {
                toast.success('Subscription config saved!');
            }
            await loadConfig();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    const handleRemoveSubscriber = async (subId) => {
        if (!await confirm({ title: 'Remove subscriber?', message: 'Remove this subscriber from the channel?', confirmLabel: 'Remove', danger: true })) return;
        try {
            await api.post('/api/subscriptions/remove', { token, subscriber_id: subId });
            toast.success('Subscriber removed');
            loadSubscribers(subsPage);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to remove');
        }
    };

    const updatePlan = (key, field, value) => {
        setPlans(prev => prev.map(p =>
            p.interval_key === key ? { ...p, [field]: value } : p
        ));
    };

    // Compute unsaved changes
    const isDirty = (() => {
        const s = savedConfigRef.current;
        if (!s) return false;
        if (s.enabled !== enabled) return true;
        if (s.promoText !== promoText) return true;
        if (s.ctaLabel !== ctaLabel) return true;
        if (s.promoBanner !== promoBanner) return true;
        if (s.vipChatTitle !== vipChatTitle) return true;
        for (const sp of s.plans) {
            const cp = plans.find(p => p.interval_key === sp.interval_key);
            if (!cp) return true;
            if (String(cp.price_usd) !== String(sp.price_usd)) return true;
            if (cp.enabled !== sp.enabled) return true;
            if (String(cp.original_price_usd || '') !== String(sp.original_price_usd || '')) return true;
            if (String(cp.initial_price_usd || '') !== String(sp.initial_price_usd || '')) return true;
            if (String(cp.promo_code || '') !== String(sp.promo_code || '')) return true;
            if (cp.show_discount_code !== sp.show_discount_code) return true;
        }
        return false;
    })();

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
                <div style={{ width: '32px', height: '32px', border: '3px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            </div>
        );
    }

    return (
        <div style={{ fontFamily: fontStack }}>
            {/* Setup */}
            <div style={card}>
                <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 600, color: 'var(--text)' }}>
                    <Icons.Settings style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                    Channel Setup
                </h3>
                {vipChatId ? (
                    <div>
                        <span style={label}>Connected Channel</span>
                        <div style={{
                            padding: '10px 14px', borderRadius: '8px',
                            background: 'var(--success-bg)', border: '1px solid var(--success-border)',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        }}>
                            <div>
                                <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text)' }}>
                                    {vipChatTitle || 'Subscription Channel'}
                                </div>
                                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                                    ID: {vipChatId}
                                </div>
                            </div>
                            <button
                                onClick={requestConnectVip}
                                disabled={connectingVip}
                                style={{ ...btnSmall, fontSize: '11px', padding: '5px 12px' }}
                            >
                                {connectingVip ? 'Waiting...' : 'Change'}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div>
                        <button
                            onClick={requestConnectVip}
                            disabled={connectingVip}
                            style={{
                                ...btnPrimary,
                                width: 'auto', padding: '12px 28px', borderRadius: '10px',
                                opacity: connectingVip ? 0.7 : 1,
                            }}
                        >
                            {connectingVip ? 'Waiting for selection...' : 'Connect Channel'}
                        </button>
                        {connectingVip && (
                            <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '8px 0 0' }}>
                                Check your Telegram and select a group. This page will update automatically.
                            </p>
                        )}
                    </div>
                )}
                <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <button
                        onClick={() => setEnabled(!enabled)}
                        style={{
                            width: '44px', height: '24px', borderRadius: '12px', border: 'none',
                            background: enabled ? 'var(--accent)' : 'var(--border)',
                            cursor: 'pointer', position: 'relative', transition: 'background 0.2s',
                        }}
                    >
                        <div style={{
                            width: '18px', height: '18px', borderRadius: '50%', background: '#fff',
                            position: 'absolute', top: '3px',
                            left: enabled ? '23px' : '3px', transition: 'left 0.2s',
                        }} />
                    </button>
                    <span style={{ fontSize: '13px', color: 'var(--text)' }}>
                        {enabled ? 'Subscriptions enabled' : 'Subscriptions disabled'}
                    </span>
                </div>
            </div>

            {/* Plans */}
            <div style={card}>
                <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 600, color: 'var(--text)' }}>
                    <Icons.DollarSign style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                    Subscription Plans
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {INTERVALS.map(interval => {
                        const plan = plans.find(p => p.interval_key === interval.key) || {};
                        const isExpanded = expandedPlans[interval.key];
                        const hasDiscount = plan.original_price_usd || plan.initial_price_usd || plan.promo_code;
                        const periodName = interval.period || 'month';
                        return (
                            <div key={interval.key} style={{
                                borderRadius: '10px',
                                border: '1px solid var(--border)',
                                background: plan.enabled ? 'var(--sidebar-item-active)' : 'transparent',
                                overflow: 'hidden',
                            }}>
                                {/* Main row */}
                                <div style={{
                                    display: 'flex', alignItems: 'center', gap: '12px',
                                    padding: '12px 14px',
                                }}>
                                    <button
                                        onClick={() => updatePlan(interval.key, 'enabled', !plan.enabled)}
                                        style={{
                                            width: '36px', height: '20px', borderRadius: '10px', border: 'none',
                                            background: plan.enabled ? 'var(--accent)' : 'var(--border)',
                                            cursor: 'pointer', position: 'relative', flexShrink: 0,
                                            transition: 'background 0.2s',
                                        }}
                                    >
                                        <div style={{
                                            width: '14px', height: '14px', borderRadius: '50%', background: '#fff',
                                            position: 'absolute', top: '3px',
                                            left: plan.enabled ? '19px' : '3px', transition: 'left 0.2s',
                                        }} />
                                    </button>
                                    <span style={{ width: '80px', fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>
                                        {interval.label}
                                    </span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>$</span>
                                        <input
                                            style={{ ...dInput, width: '80px' }}
                                            type="number"
                                            step="0.01"
                                            min="1"
                                            placeholder="1.00"
                                            value={plan.price_usd}
                                            onChange={e => updatePlan(interval.key, 'price_usd', e.target.value)}
                                            title="Price per billing cycle (min $1)"
                                        />
                                    </div>
                                    <div style={{ flex: 1 }} />
                                    {hasDiscount && !isExpanded && (
                                        <span style={{
                                            fontSize: '10px', color: 'var(--text-muted)',
                                            background: 'var(--border)', borderRadius: '4px',
                                            padding: '2px 6px', whiteSpace: 'nowrap',
                                        }}>
                                            {plan.promo_code || 'discounted'}
                                        </span>
                                    )}
                                    {plan.enabled && (
                                        <button
                                            onClick={() => setExpandedPlans(prev => ({ ...prev, [interval.key]: !prev[interval.key] }))}
                                            style={{
                                                background: 'none', border: 'none', cursor: 'pointer',
                                                padding: '4px 6px', borderRadius: '6px', color: 'var(--text-muted)',
                                                fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px',
                                                transition: 'color 0.15s',
                                            }}
                                            title="Discount & promo settings"
                                        >
                                            <span style={{ fontSize: '11px' }}>Discount</span>
                                            <span style={{ fontSize: '14px', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>&#9662;</span>
                                        </button>
                                    )}
                                </div>

                                {/* Expandable discount section */}
                                {plan.enabled && isExpanded && (
                                    <div style={{
                                        padding: '0 14px 14px',
                                        borderTop: '1px solid var(--border)',
                                        marginTop: '-1px',
                                    }}>
                                        <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: '10px 0 12px', lineHeight: '1.5' }}>
                                            Show a crossed-out old price and offer a first-period discount. Customers enter the discount code at checkout.
                                        </p>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                                            <div>
                                                <span style={{ ...label, fontSize: '11px', marginBottom: '4px' }}>Old price</span>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>$</span>
                                                    <input
                                                        style={{ ...dInput, width: '100%', fontSize: '13px' }}
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        placeholder="e.g. 29.99"
                                                        value={plan.original_price_usd}
                                                        onChange={e => updatePlan(interval.key, 'original_price_usd', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <span style={{ ...label, fontSize: '11px', marginBottom: '4px' }}>First {periodName} price</span>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>$</span>
                                                    <input
                                                        style={{ ...dInput, width: '100%', fontSize: '13px' }}
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        placeholder="e.g. 4.99"
                                                        value={plan.initial_price_usd}
                                                        onChange={e => updatePlan(interval.key, 'initial_price_usd', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <span style={{ ...label, fontSize: '11px', marginBottom: '4px' }}>Discount code</span>
                                                <input
                                                    style={{ ...dInput, width: '100%', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}
                                                    type="text"
                                                    placeholder="e.g. VIP50OFF"
                                                    value={plan.promo_code}
                                                    onChange={e => updatePlan(interval.key, 'promo_code', e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))}
                                                />
                                            </div>
                                        </div>
                                        {plan.promo_code && (
                                            <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <button
                                                    onClick={() => updatePlan(interval.key, 'show_discount_code', !plan.show_discount_code)}
                                                    style={{
                                                        width: '32px', height: '18px', borderRadius: '9px', border: 'none',
                                                        background: plan.show_discount_code ? 'var(--accent)' : 'var(--border)',
                                                        cursor: 'pointer', position: 'relative', flexShrink: 0,
                                                        transition: 'background 0.2s',
                                                    }}
                                                >
                                                    <div style={{
                                                        width: '12px', height: '12px', borderRadius: '50%', background: '#fff',
                                                        position: 'absolute', top: '3px',
                                                        left: plan.show_discount_code ? '17px' : '3px', transition: 'left 0.2s',
                                                    }} />
                                                </button>
                                                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                                                    Show discount code on subscribe page
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Plan overview */}
                {plans.some(p => p.enabled && parseFloat(p.price_usd) > 0) && (
                    <div style={{
                        marginTop: '16px', padding: '14px',
                        borderRadius: '10px', border: '1px dashed var(--border)',
                        background: 'var(--bg)',
                    }}>
                        <span style={{ ...label, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>
                            Preview &mdash; how subscribers see your plans
                        </span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {plans.filter(p => p.enabled && parseFloat(p.price_usd) > 0).map(p => {
                                const intv = INTERVALS.find(i => i.key === p.interval_key);
                                const price = parseFloat(p.price_usd);
                                const orig = parseFloat(p.original_price_usd);
                                const init = parseFloat(p.initial_price_usd);
                                const showCode = p.show_discount_code !== false;
                                const periodName = intv?.period || 'month';
                                return (
                                    <div key={p.interval_key} style={{
                                        padding: '10px 14px', borderRadius: '8px',
                                        border: '1px solid var(--border)', background: 'var(--card-bg)',
                                        minWidth: '140px', flex: '1',
                                    }}>
                                        <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text)', marginBottom: '4px' }}>
                                            {intv?.label}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                                            {orig > price && (
                                                <span style={{ fontSize: '12px', color: 'var(--text-muted)', textDecoration: 'line-through' }}>
                                                    ${orig.toFixed(2)}
                                                </span>
                                            )}
                                            <span style={{ fontSize: '18px', fontWeight: 700, color: 'var(--accent)' }}>
                                                ${price.toFixed(2)}
                                            </span>
                                        </div>
                                        {showCode && !isNaN(init) && init < price && (
                                            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                                                {init === 0 ? `First ${periodName} FREE` : `First ${periodName} $${init.toFixed(2)}`}
                                                {p.promo_code && (
                                                    <span style={{
                                                        marginLeft: '4px', fontWeight: 600,
                                                        color: '#065f46', background: '#ecfdf5',
                                                        border: '1px solid #a7f3d0', borderRadius: '3px',
                                                        padding: '1px 4px', fontSize: '10px',
                                                        fontFamily: 'SF Mono, SFMono-Regular, Consolas, monospace',
                                                    }}>
                                                        {p.promo_code}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Promo */}
            <div style={card}>
                <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 600, color: 'var(--text)' }}>
                    Promo Settings
                </h3>
                <div style={{ marginBottom: '16px' }}>
                    <span style={label}>Promo Banner</span>
                    <input
                        type="file"
                        ref={bannerInputRef}
                        onChange={handleBannerUpload}
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        style={{ display: 'none' }}
                    />
                    {promoBanner ? (
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                            <img
                                src={`https://api.onlywheels.org/api/dashboard/file?file_id=${encodeURIComponent(promoBanner)}`}
                                alt="Promo banner"
                                style={{ width: '200px', height: 'auto', borderRadius: '8px', border: '1px solid var(--border)', objectFit: 'cover' }}
                            />
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <button
                                    onClick={() => bannerInputRef.current?.click()}
                                    disabled={uploadingBanner}
                                    style={{ ...btnSmall, fontSize: '11px', padding: '5px 12px' }}
                                >
                                    {uploadingBanner ? 'Uploading...' : 'Change'}
                                </button>
                                <button
                                    onClick={() => setPromoBanner('')}
                                    style={{ ...btnSmall, fontSize: '11px', padding: '5px 12px', color: 'var(--error)' }}
                                >
                                    Remove
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button
                            onClick={() => bannerInputRef.current?.click()}
                            disabled={uploadingBanner}
                            style={{
                                ...btnSmall, padding: '10px 16px', fontSize: '12px',
                                border: '2px dashed var(--border)', background: 'transparent',
                                color: 'var(--text-muted)', borderRadius: '8px',
                                opacity: uploadingBanner ? 0.7 : 1,
                            }}
                        >
                            {uploadingBanner ? 'Uploading...' : 'Upload Banner Image'}
                        </button>
                    )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div>
                        <span style={label}>Promo Text</span>
                        <textarea
                            style={{ ...dInput, minHeight: '80px', resize: 'vertical' }}
                            placeholder="Get exclusive content..."
                            value={promoText}
                            onChange={e => setPromoText(e.target.value)}
                        />
                    </div>
                    <div>
                        <span style={label}>CTA Button Label</span>
                        <input
                            style={dInput}
                            type="text"
                            placeholder="Subscribe Now"
                            value={ctaLabel}
                            onChange={e => setCtaLabel(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            {/* Save + Post */}
            <div style={{ marginBottom: '24px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                <button
                    onClick={handleSave}
                    disabled={saving}
                    style={{ ...btnPrimary, width: 'auto', padding: '12px 32px', borderRadius: '10px', opacity: saving ? 0.7 : 1 }}
                >
                    {saving ? 'Saving...' : 'Save Configuration'}
                </button>
                {isDirty && (
                    <span style={{
                        fontSize: '12px', fontWeight: 600, color: '#b45309',
                        background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)',
                        padding: '4px 10px', borderRadius: '6px',
                    }}>
                        Unsaved changes
                    </span>
                )}
                <button
                    onClick={handlePostPromo}
                    disabled={posting || !vipChatId}
                    style={{
                        ...btnSmall, padding: '12px 24px', borderRadius: '10px', fontSize: '14px',
                        opacity: (posting || !vipChatId) ? 0.5 : 1,
                    }}
                >
                    {posting ? 'Posting...' : 'Post Promo to Channel'}
                </button>
            </div>

            {/* Subscribers */}
            <div style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', gap: '0', borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--border)' }}>
                        {[{ key: 'active', label: 'Active Subscribers' }, { key: 'expired', label: 'Expired Subscribers' }].map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => switchSubsTab(tab.key)}
                                style={{
                                    padding: '8px 16px', fontSize: '13px', fontWeight: 600,
                                    border: 'none', cursor: 'pointer',
                                    background: subsTab === tab.key ? 'var(--accent)' : 'transparent',
                                    color: subsTab === tab.key ? '#fff' : 'var(--text-muted)',
                                    transition: 'all 0.15s',
                                }}
                            >
                                {tab.label} {subsTab === tab.key ? `(${subsTotal})` : ''}
                            </button>
                        ))}
                    </div>
                    <button onClick={() => loadSubscribers(subsPage)} style={btnGhost}>
                        Refresh
                    </button>
                </div>
                {subsLoading ? (
                    <div style={{ textAlign: 'center', padding: '30px' }}>
                        <div style={{ width: '24px', height: '24px', border: '2px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
                    </div>
                ) : subscribers.length === 0 ? (
                    <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '30px 0', fontSize: '13px' }}>
                        {subsTab === 'active' ? 'No active subscribers' : 'No expired subscribers'}
                    </p>
                ) : (
                    <>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                            {subscribers.map(sub => {
                                const sc = STATUS_COLORS[sub.status] || STATUS_COLORS.pending;
                                const intervalInfo = INTERVALS.find(i => i.key === sub.interval_key);
                                const isExpanded = expandedSub === sub.id;
                                const details = subDetails[sub.id];
                                const detailsLoading = subDetailsLoading[sub.id];
                                return (
                                    <div key={sub.id} style={{ borderBottom: '1px solid var(--border)' }}>
                                        <div
                                            onClick={() => toggleSubDetails(sub.id)}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: '12px',
                                                padding: '12px 10px', cursor: 'pointer',
                                                background: isExpanded ? 'var(--sidebar-item-active)' : 'transparent',
                                                transition: 'background 0.15s',
                                            }}
                                        >
                                            <span style={{ fontSize: '12px', color: 'var(--text-muted)', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>&#9654;</span>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <span style={{ fontWeight: 500, fontSize: '13px', color: 'var(--text)' }}>
                                                    {sub.tg_username ? `@${sub.tg_username}` : sub.tg_user_id || 'Not connected'}
                                                </span>
                                                <span style={{ fontSize: '12px', color: 'var(--text-muted)', marginLeft: '8px' }}>
                                                    {intervalInfo ? intervalInfo.label : sub.interval_key}
                                                </span>
                                            </div>
                                            <span style={{
                                                display: 'inline-block', padding: '3px 10px',
                                                borderRadius: '20px', fontSize: '11px', fontWeight: 600,
                                                background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`,
                                            }}>
                                                {sub.status}
                                            </span>
                                            {subsTab === 'active' && (
                                                <button
                                                    onClick={e => { e.stopPropagation(); handleRemoveSubscriber(sub.id); }}
                                                    style={{ ...btnDanger, padding: '4px 10px', fontSize: '11px', borderRadius: '6px' }}
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>
                                        {isExpanded && (
                                            <div style={{
                                                padding: '0 10px 14px 34px',
                                                background: 'var(--sidebar-item-active)',
                                            }}>
                                                {detailsLoading ? (
                                                    <div style={{ padding: '10px 0' }}>
                                                        <div style={{ width: '18px', height: '18px', border: '2px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                                                    </div>
                                                ) : details ? (
                                                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: '1.8' }}>
                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 24px', maxWidth: '500px' }}>
                                                            <span style={{ color: 'var(--text)', fontWeight: 600 }}>Subscribed</span>
                                                            <span>{sub.created_at ? new Date(sub.created_at).toLocaleDateString() : '-'}</span>

                                                            <span style={{ color: 'var(--text)', fontWeight: 600 }}>Last payment</span>
                                                            <span>{details.subscriber?.last_payment_at ? new Date(details.subscriber.last_payment_at).toLocaleDateString() : '-'}</span>

                                                            {details.membership?.renewal_period_end && (
                                                                <>
                                                                    <span style={{ color: 'var(--text)', fontWeight: 600 }}>Next billing</span>
                                                                    <span>{new Date(details.membership.renewal_period_end).toLocaleDateString()}</span>
                                                                </>
                                                            )}

                                                            {details.membership?.cancel_at_period_end && (
                                                                <>
                                                                    <span style={{ color: 'var(--text)', fontWeight: 600 }}>Cancels at</span>
                                                                    <span style={{ color: 'var(--error)' }}>End of period</span>
                                                                </>
                                                            )}

                                                            {details.membership?.user?.email && (
                                                                <>
                                                                    <span style={{ color: 'var(--text)', fontWeight: 600 }}>Email</span>
                                                                    <span>{details.membership.user.email}</span>
                                                                </>
                                                            )}

                                                            {details.membership?.user?.name && (
                                                                <>
                                                                    <span style={{ color: 'var(--text)', fontWeight: 600 }}>Name</span>
                                                                    <span>{details.membership.user.name}</span>
                                                                </>
                                                            )}

                                                            {details.membership?.status && (
                                                                <>
                                                                    <span style={{ color: 'var(--text)', fontWeight: 600 }}>Membership status</span>
                                                                    <span>{details.membership.status}</span>
                                                                </>
                                                            )}
                                                        </div>

                                                        {details.payments && details.payments.length > 0 && (
                                                            <div style={{ marginTop: '12px' }}>
                                                                <span style={{ color: 'var(--text)', fontWeight: 600, fontSize: '12px' }}>Payment history</span>
                                                                <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                                    {details.payments.map((p, i) => (
                                                                        <div key={p.id || i} style={{
                                                                            display: 'flex', alignItems: 'center', gap: '10px',
                                                                            padding: '6px 10px', borderRadius: '6px',
                                                                            background: 'var(--bg)', border: '1px solid var(--border)',
                                                                        }}>
                                                                            <span style={{ fontWeight: 600, color: p.status === 'paid' ? 'var(--success)' : 'var(--error)' }}>
                                                                                ${typeof p.amount === 'number' ? p.amount.toFixed(2) : p.amount || '?'}
                                                                            </span>
                                                                            <span style={{ flex: 1 }}>
                                                                                {p.paid_at ? new Date(p.paid_at).toLocaleDateString() : p.created_at ? new Date(p.created_at).toLocaleDateString() : '-'}
                                                                            </span>
                                                                            <span style={{
                                                                                padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 600,
                                                                                background: p.status === 'paid' ? 'var(--success-bg)' : 'var(--error-bg)',
                                                                                color: p.status === 'paid' ? 'var(--success)' : 'var(--error)',
                                                                            }}>
                                                                                {p.status}
                                                                            </span>
                                                                            {p.card_brand && (
                                                                                <span style={{ fontSize: '10px' }}>
                                                                                    {p.card_brand} ****{p.card_last4}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}

                                                    </div>
                                                ) : (
                                                    <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                                                        No details available
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        {subsTotal > 20 && (
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
                                <button
                                    onClick={() => loadSubscribers(subsPage - 1)}
                                    disabled={subsPage <= 1}
                                    style={{ ...btnSmall, opacity: subsPage <= 1 ? 0.4 : 1 }}
                                >
                                    Prev
                                </button>
                                <span style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: '32px' }}>
                                    Page {subsPage} of {Math.ceil(subsTotal / 20)}
                                </span>
                                <button
                                    onClick={() => loadSubscribers(subsPage + 1)}
                                    disabled={subsPage >= Math.ceil(subsTotal / 20)}
                                    style={{ ...btnSmall, opacity: subsPage >= Math.ceil(subsTotal / 20) ? 0.4 : 1 }}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
            <ConfirmModal {...confirmProps} />
        </div>
    );
}
